import { Box, Button, Typography } from "@mui/material";
import { MainContainer } from "../../components/MainContainerComp";
import { ProgressBar } from "../../components/ProgressBar";
import { theme } from "../../utils/theme";
import { VerifyEmail } from "./components/VerifyEmail";
import { UpdatePhone } from "./components/UpdatePhone";
import { Confirmation } from "./components/Confirmation";
import {
  IContactEmailData,
  IContactPhoneNumberData,
} from "./ContactPhoneNumberData";
import { ProgressBarSteps } from "./ContactPhoneNumberModels";
import { useState } from "react";

interface IContactPhoneProps {
  data: IContactEmailData;
  submitting: boolean;
  activeStep: string;
  onChange: (field: keyof IContactPhoneNumberData, newValue: string) => void;
  email: string;
  phones: IContactPhoneNumberData;
  onSubmit: () => void;
  onNext: () => void;
  isNextDisabled: boolean;
  isFormTouched: boolean;
  submissionSuccess: boolean;
  hasVerifiedEmail: boolean;
  isValidEmail: (email: string) => boolean;
  onSetEmail: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  onSubmitEmail: () => void;
  onClose: () => void;
}

export const ContactPhone: React.FC<IContactPhoneProps> = ({
  data,
  submitting,
  activeStep,
  onChange,
  email,
  phones,
  onSubmit,
  onNext,
  isFormTouched,
  hasVerifiedEmail,
  isValidEmail,
  onSetEmail,
  submissionSuccess,
  onSubmitEmail,
  onClose,
}) => {
  const isTheLastStep = activeStep === ProgressBarSteps.Confirmation;
  const isTheFirstStep = activeStep === ProgressBarSteps.VerifyEmail;
  const readOnly = hasVerifiedEmail || submissionSuccess;
  const emailError =
    (!email && isFormTouched) || (!!email && !isValidEmail(email));
  const [phoneError, setPhoneError] = useState(false);

  return (
    <MainContainer
      hasGreyBg
    >
        <ProgressBar
          steps={Object.values(ProgressBarSteps)}
          activeStep={activeStep}
        />
        {activeStep === ProgressBarSteps.VerifyEmail && (
          <>
            <Typography
              component="div"
              sx={{
                fontWeight: "bold",
                color: theme.palette.corporateBlue,
                textAlign: "left",
                ml: 2,
                pt: 2,
                pb: 3,
              }}
            >
              This Update feature requires a verified email. Please provide an
              email address.
            </Typography>
            <VerifyEmail
              data={data}
              email={email}
              emailError={emailError}
              onSetEmail={onSetEmail}
              submissionSuccess={submissionSuccess}
              readOnly={readOnly}
            />
          </>
        )}
        {activeStep === ProgressBarSteps.UpdatePhone && (
          <UpdatePhone
            phoneNumbers={phones}
            onChange={onChange}
            onErrorChange={setPhoneError}
          />
        )}
        {activeStep === ProgressBarSteps.Confirmation && <Confirmation />}
      <Box
        sx={{
          display: "flex",
          justifyContent: isTheFirstStep ? "space-between" : "center",
          mr: isTheFirstStep ? { md: "17%" } : 0,
          ml: isTheFirstStep ? { md: "17%" } : 0,
          px: 2,
          mb: 10,
        }}
      >
        {isTheFirstStep && (
          <Button
            disabled={
              submitting ||
              emailError ||
              !isFormTouched ||
              submissionSuccess ||
              hasVerifiedEmail
            }
            size="large"
            variant="contained"
            onClick={onSubmitEmail}
            data-automation="update-btn"
          >
            Update
          </Button>
        )}
        {isTheFirstStep && (
          <Button
            disabled={!hasVerifiedEmail}
            size="large"
            variant="contained"
            onClick={onNext}
            data-automation="next-btn"
          >
            Next
          </Button>
        )}
        {!isTheFirstStep && !isTheLastStep && (
          <Button
            disabled={phoneError}
            size="large"
            variant="contained"
            onClick={onSubmit}
            data-automation="submit-btn"
          >
            Submit
          </Button>
        )}
        {isTheLastStep && (
          <Button
            size="large"
            variant="contained"
            onClick={onClose}
            data-automation="close-btn"
          >
            Close
          </Button>
        )}
      </Box>
    </MainContainer>
  );
};
